import * as vp from 'vopay';
import * as Yup from 'yup';
import moment from 'moment';
import { Formik } from 'formik';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaylink } from '@/providers/Paylink.provider';
import { useLayout } from '@/providers/Layout.provider';
import { PartialPaymentValues } from './types';
import styles from './PartialPayment.module.scss';

const { Box, Button, DatepickerSimple, ErrorText, Accordion, ElementContainer, TextField } = vp;

type PartialPaymentFormProps = {
  isModalOpen: boolean;
  defaultValues: PartialPaymentValues;
  onPartialPayment: (val: PartialPaymentValues) => void;
  remainingAmount: PartialPaymentValues['Amount'];
  onClose: (val: boolean) => void;
  onAmountChange: (val: PartialPaymentValues['Amount']) => void;
};

type FC = FunctionComponent<PartialPaymentFormProps>;

const PartialPaymentForm: FC = ({
  isModalOpen,
  defaultValues,
  onPartialPayment,
  remainingAmount,
  onClose,
  onAmountChange,
}: PartialPaymentFormProps) => {
  const { color } = useLayout();
  const { paylink } = usePaylink();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'PartialPayment' });
  const [calendarOpen, setCalendarOpen] = useState(false);
  return (
    <div className={styles.formWrapper} style={{ overflow: calendarOpen ? 'visible' : 'auto' }}>
      <Formik initialValues={defaultValues} onSubmit={onPartialPayment} validationSchema={createdValidationSchema()}>
        {({ handleChange, handleBlur, handleSubmit, setErrors, isValid, values, errors, touched, resetForm }) => {
          const [formattedRemainderDate, setFormattedRemainderDate] = useState('');

          useEffect(() => {
            if (values.Amount && Number(vp.sanitizeToNumeric(values.Amount)) > 0) {
              onAmountChange(vp.sanitizeToNumeric(values.Amount) || null);
            } else {
              onAmountChange(null);
            }
          }, [values.Amount]);

          const isButtonEnabled =
            paylink.RemainderPaymentDate !== ''
              ? Number(vp.sanitizeToNumeric(values.Amount || '0')) > 0 && !!values.RemainderDate && isValid
              : Number(vp.sanitizeToNumeric(values.Amount || '0')) > 0 && isValid;

          useEffect(() => {
            if (values.Amount && Number(values.Amount) > 0) {
              setErrors({
                Amount: '',
                RemainderDate: '',
              });
            }
          }, [values]);

          useEffect(() => {
            if (values.RemainderDate !== '') {
              const date = new Date(Number(values.RemainderDate));
              const formattedDate = moment(date.toDateString().substring(4)).format('MMM DD, YYYY');

              setFormattedRemainderDate(formattedDate);
            }
          }, [values.RemainderDate]);

          useEffect(() => {
            if (!isModalOpen) {
              resetForm();
              onAmountChange(null);
            }
          }, [isModalOpen]);

          return (
            <form style={{ width: '100%' }} onSubmit={handleSubmit}>
              <Box flx="col" gapSize="16px">
                <div className={styles.formTitle}>
                  {t('enterAmount')}
                  <div className={styles.buttonContainer}>
                    <Button
                      id="close-form-button"
                      label=""
                      icon="close2"
                      iconWidth="13px"
                      iconHeight="13px"
                      iconColor="primary-dark"
                      iconLocation="center"
                      width="20px"
                      background="transparent"
                      noPadding
                      noShadow
                      color="none"
                      onClick={() => {
                        onClose(false);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.amountContainer}>
                  <div className={styles.totalTitle}>{t('total')}</div>
                  <div className={styles.totalAmountPopup}>
                    {paylink.Currency} {vp.formattedCurrency(paylink.Amount)}
                  </div>
                  <div className={styles.totalRemaining}>
                    {`${t('remaining')} ${paylink.Currency} ${
                      Number(vp.sanitizeToNumeric(remainingAmount || '')) >= 0
                        ? vp.formattedCurrency(remainingAmount || '')
                        : vp.formattedCurrency(paylink.Amount || '')
                    }`}
                  </div>
                </div>
                <Box flx="col" height="auto">
                  <Box flx="col" gapSize="16px" height="auto">
                    <ElementContainer
                      isBold={false}
                      color="dark-grey"
                      padding="0 0 2px 0"
                      label={
                        <Box flx="row" ai="fs">
                          <span className={styles.formLabel}>{t('payNow')}</span> {getRequiredIcon(true)}
                        </Box>
                      }
                      input={
                        <Box flx="col" gapSize="8px">
                          <TextField
                            name={vp.PartialPaymentId.Amount}
                            value={String(values.Amount)}
                            variant="lined"
                            preset="currency"
                            height="47px"
                            borderColor={errors.Amount && (touched.Amount || !!values.Amount) ? '#E24C4C' : '#C4C4C4'}
                            placeholder="0.00"
                            padding="12px 10px"
                            elementLeft={<span className={styles.currency}>{paylink.Currency}</span>}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                          <div className={styles.amountDesc}>
                            {t('minimumAmount')} {paylink.Currency} {paylink.MinimumPaymentAccepted}
                          </div>
                          {errors.Amount && (touched.Amount || !!values.Amount) && (
                            <ErrorText
                              error={errors.Amount && (touched.Amount || !!values.Amount) ? errors.Amount : null}
                              fontSize="14px"
                              indentation={false}
                              fitContent
                            />
                          )}
                        </Box>
                      }
                    />
                    <ElementContainer
                      isBold={false}
                      color="dark-grey"
                      padding="0 0 2px 0"
                      label={
                        <Box flx="row" ai="fs">
                          <span className={styles.formLabel}>{t('scheduleRemainingAmount')}</span>{' '}
                          {getRequiredIcon(true)}
                        </Box>
                      }
                      input={
                        <Box flx="col" gapSize="8px">
                          <DatepickerSimple
                            name={vp.PartialPaymentId.RemainderDate}
                            value={String(values.RemainderDate)}
                            variant="lined"
                            placeholder="MMM-DD-YYYY"
                            placeholderColor="#949494"
                            borderColor="#C4C4C4"
                            onBlur={handleBlur}
                            onOpen={setCalendarOpen}
                            position="topCenter"
                            iconPosition="left"
                            format="date-comma"
                            min={moment().valueOf() as vp.Timestamp}
                            max={
                              paylink.RemainderPaymentDate
                                ? (moment(paylink.RemainderPaymentDate, 'YYYY-MM-DD').valueOf() as vp.Timestamp)
                                : (moment().add(1, 'year').valueOf() as vp.Timestamp)
                            }
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              if (e.target && e.target.value === undefined) {
                                e.target.value = String(values.RemainderDate);
                              }
                              handleChange(e);
                            }}
                          />
                          <ErrorText
                            error={errors.RemainderDate && touched.RemainderDate ? errors.RemainderDate : null}
                            size="lg"
                            indentation={false}
                            fitContent
                          />
                        </Box>
                      }
                    />
                  </Box>
                </Box>
                {formattedRemainderDate !== '' &&
                  values.Amount &&
                  Number(vp.sanitizeToNumeric(values.Amount)) > 0 &&
                  isValid && (
                    <Box flx="col" gapSize="16px">
                      <div className={styles.timelineContainer}>
                        <Accordion
                          style={{
                            '--background': '#f6f6f6',
                          }}
                          title={<div className={styles.timelineTitle}>2 {t('payments')}</div>}
                          angleSize="11px"
                          body={
                            <div className={styles.timelines}>
                              <Box ai="center" jc="sb">
                                <Box gapSize="8px" ai="center">
                                  <span className={[styles.dot, styles.active].join(' ')} />
                                  <span>{t('dueToday')}</span>
                                </Box>
                                <span>
                                  {paylink.Currency} {vp.formattedCurrency(vp.sanitizeToNumeric(values.Amount) || '')}
                                </span>
                              </Box>
                              <Box ai="center" jc="sb">
                                <Box gapSize="8px" ai="center">
                                  <span className={styles.dot} />
                                  <span>{formattedRemainderDate}</span>
                                </Box>
                                <span>
                                  {paylink.Currency} {vp.formattedCurrency(remainingAmount || '')}
                                </span>
                              </Box>
                            </div>
                          }
                        />
                      </div>
                      <div className={styles.timeDesc}>
                        {i18n.language === 'en' ? (
                          <div>
                            You will be charged{' '}
                            <b>
                              {paylink.Currency} {vp.formattedCurrency(vp.sanitizeToNumeric(values.Amount) || '')}
                            </b>{' '}
                            right now and the remaining amount will be charged on <b>{formattedRemainderDate}</b>
                          </div>
                        ) : (
                          <div>
                            Vous serez facturé <b>{vp.formattedCurrency(vp.sanitizeToNumeric(values.Amount) || '')}</b>{' '}
                            {paylink.Currency} immédiatement et le montant restant sera facturé le{' '}
                            <b>{formattedRemainderDate}.</b>
                          </div>
                        )}
                      </div>
                    </Box>
                  )}
                <Box width="100%" jc="center">
                  <Button
                    id="continue-partial-payment-button"
                    label={`${t('continue')}`}
                    type="submit"
                    background={color}
                    textColor={vp.getContrastColor(color)}
                    width="100%"
                    height="47px"
                    textSize={15}
                    fontUnit="px"
                    round
                    bold
                    noShadow
                    isDisabled={!isButtonEnabled}
                    opacity={isButtonEnabled ? '1' : '0.5'}
                  />
                </Box>
              </Box>
            </form>
          );
        }}
      </Formik>
    </div>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Amount: Yup.number()
        .nullable()
        .transform((_value, originalValue: string) => Number(vp.sanitizeToNumeric(originalValue)))
        .test(
          'required',
          `${t('requiredFormFieldError')}`,
          (value) => value !== null && value !== undefined && value === null,
        )
        .test('exceed-amount-validation', `${t('totalAmountError')}`, (value) => {
          const amountValidation = Number(value) > Number(paylink.Amount);
          return !amountValidation;
        })
        .test(
          'minimum-amount-validation',
          `${t('minimumAmountError')} ${paylink.Currency} ${String(paylink.MinimumPaymentAccepted)}`,
          (value) => {
            const amountValidation = Number(value) < Number(paylink.MinimumPaymentAccepted);
            return !amountValidation;
          },
        )
        .required(`${t('requiredFormFieldError')}`),
      RemainderDate:
        paylink.RemainderPaymentDate !== '' ? Yup.string().required(`${t('requiredFormFieldError')}`) : Yup.string(),
    });
  }
  function getRequiredIcon(isRequired: boolean): React.JSX.Element {
    return isRequired ? <span style={{ color: '#EB001B' }}>*</span> : <></>;
  }
};

export default PartialPaymentForm;
